<template>
<div id="namespace-tree">
    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div class="text-right">
        <div v-if="!submit_loading">
            <button class="btn btn-secondary mr-1" :class="{'btn-sm text-sm': is_mobile}" @click="$emit('close')">Cancel</button>
            <button class="btn btn-brand text-bold" :class="{'btn-sm text-sm': is_mobile}"  @click="submit()" :disabled="!selected_folder">{{ submit_btn_label }}</button>
        </div>
        <i v-else class="ion-load-c spin"></i>
    </div>

    <div class="dd mt-2 mb-2">
        
        <template v-if="false && is_client_space_client == false && is_move_operation">
            <!-- (disabled) Non-clients can move stuff to a different namespace -->
            <select class="form-control mb-3"  v-model="target_namespace">
                <option :value="'team_files'">Team Files</option>
                <option :value="'admin_folder'" v-if="is_user_admin">Admin Folder</option>
                <optgroup label="Client Spaces" v-if="my_client_spaces">
                    <option 
                        v-for="space in my_client_spaces.filter(s => !s.is_archived)" 
                        :key="space.key" 
                        :value="space">{{ space.client_name }}</option>
                </optgroup>
            </select>
        </template>
        
        <div v-if="namespace_loading" class="d-flex align-items-center justify-content-center gap-1">
            <span>Loading</span>
            <i class="ion-load-c spin"></i>
        </div>
        <namespace-tree-row 
            v-else-if="namespace"
            :orig_parent_id="targetFile.parent_id"
            :loaded_namespace_id="`${ns_type}-${ns_key||''}`"
            :children="namespace" 
            :clicked="tree_folder_selected"
        />
    </div>

    <div class="text-right">
        <div v-if="!submit_loading">
            <button class="btn btn-secondary mr-1" :class="{'btn-sm text-sm': is_mobile}" @click="$emit('close')">Cancel</button>
            <button class="btn btn-brand text-bold" :class="{'btn-sm text-sm': is_mobile}" @click="submit()" :disabled="!selected_folder">{{ submit_btn_label }}</button>
        </div>
        <i v-else class="ion-load-c spin"></i>
    </div>
    <div v-if="error" class="alert alert-danger mt-2">{{ error }}</div>
</div>
</template>
<script>

import { NamespaceService } from '@/services/namespace-service.js'
import { Utils } from '@/helpers/utils.js'

import namespaceTreeRow from "@/components/namespace-tree-row.vue";
import { ClientSpacesService } from '@/services/client_spaces-service';
import { bus, events } from '@/helpers/event_bus';

export default {
    props: {
        // What is the namespace operation: 'copy_file', 'copy_files', 'move_file', 'move_files', 'move_folder', 
        action: String, 
        
        // The File or Folder that is being moved/copied. Null when a batch of files are moved/copied
        targetFile: {type: Object, default: () => null}, 

        // Whether the logged-in user is a Team Admin. Allows moving to Admin Folder
        is_user_admin: {type: Boolean, default: false}, 
        
        // Whether the logged-in user is a Client Space client. When True, cross-namespace moving is not allowed.
        is_client_space_client: {type: Boolean, default: false} 
    },
    
    components: {
        namespaceTreeRow
    },

    data(){
        return {
            
            // Which namespace is loaded: 'team_files', 'admin_folder' or a Client Space namespace object
            target_namespace: null, 

            // Client Spaces accessible by the currently logged-in SkyFlok team member
            my_client_spaces: [],

            // Hierarchical folder structure of the selected namespace
            // Each folder has the following fields:
            // - name: display name
            // - id: entity ID, -1 for the root
            // - parent_id: ID of the parent folder, NULL for the root
            // - children: Array of immediate child folders 
            namespace: null,

            // Type and key of the currently loaded namespace
            // Used to authorize the actual copy/move operation
            ns_type: null, ns_key: null,
            
            // User-facing error message, used by all network operation handlers
            error: false,
            
            // Currently selected folder (name, id, parent_id)
            selected_folder: null,
            // True while the contents of the selected namespace is loading
            namespace_loading: false,
            // True while the copy/move operation is loading
            submit_loading: false
        }
    },

    computed: {
        is_mobile(){ return Utils.is_mobile()},
        is_move_operation(){ return this.action.startsWith('move_')},
        submit_btn_label(){
            return `${this.action.startsWith('copy') ? 'Copy' : 'Move' } to ${this.selected_folder?.name || '...'}`
        }
    },

    watch: {
        target_namespace(){
            // When the target namespace changes, load its folder structure
            
            if(!this.target_namespace) return 

            // Clear the selected folder
            this.selected_folder = null
            
            if(this.target_namespace.client_name){
                this.load_namespace(NamespaceService.NS_TYPE_CLIENT_SPACE, this.target_namespace.key)
            }
            else if(this.target_namespace === "team_files"){
                this.load_namespace(NamespaceService.NS_TYPE_DEFAULT)
            }
            else if(this.target_namespace === "admin_folder"){
                this.load_namespace(NamespaceService.NS_TYPE_ADMIN_FOLDER)
            }
        },
    },

    created(){
        // Load the team's Client Spaces if the user is a team member (not a space client)
        if(!this.is_client_space_client){
            ClientSpacesService.list()
                .then(res => {
                    this.my_client_spaces = res.body
                    
                    // When a Team Member loads this dialog in a Client Space, set the current Space as the
                    // selected namespace. This triggers loading its contents.
                    if(!this.target_namespace && NamespaceService.ns_type == NamespaceService.NS_TYPE_CLIENT_SPACE){
                        this.target_namespace = this.my_client_spaces.find(space => space.key == NamespaceService.ns_key)
                    }
                })
                .catch(() => console.error("Failed to load Client Spaces!"))
        }
    },

    mounted(){
        // Load the current namespace contents
        if(this.is_client_space_client){
            // Client: load the current namespace (client space)
            this.load_namespace()
        }
        else{
            // Team Member: Set the current namespace as selected. This loads the folder structure
            switch(NamespaceService.ns_type){
                case NamespaceService.NS_TYPE_DEFAULT:
                    this.target_namespace = 'team_files';
                    break;
                case NamespaceService.NS_TYPE_ADMIN_FOLDER:
                    this.target_namespace = 'admin_folder';
                    break;
                case NamespaceService.NS_TYPE_CLIENT_SPACE:
                    this.target_namespace = this.my_client_spaces && this.my_client_spaces.find(space => space.key == NamespaceService.ns_key)
                    break;
                default:
                    console.error("Unknown ns type: "+ NamespaceService.ns_type)
            }
        }

    },

    methods: {

        load_namespace(ns_type, ns_key) {
            /**
             * Loads the folders of the given namespace and compiles a hierarchy to `this.namespace`.
             * 
             * @param ns_type Type of the Namespace to load (NamespaceService.NS_TYPE_XXX)
             * @param ns_key (optional) When the namespace requires a key (e.g, Client Space),
             *  pass that key here. Not needed when:
             *      - Loading a unique namespace (team files, admin folder) with a logged in team member, or 
             *      - Loading the Space of the logged in Space Client (this is the only namespace they have access to)
             */
            this.error = false
            this.namespace_loading = true
            const load_folders_only = true
            NamespaceService.get_all_namespace(load_folders_only, ns_type, ns_key).then(res => {

                let folders = res.body
                    .filter(en => en.entity_type === NamespaceService.ENTITY_TYPE_FOLDER)
                // Add color
                folders.forEach(f => f.color = Utils.string_to_color(f.name))

                // When a folder is being moved, exclude it from the hierarchy to avoid creating a folder singularity
                const moved_folder_id = (this.action === 'move_folder') ? this.targetFile.id : null
                
                // Store the type and key of the loaded namespace, this will be used in the 
                // service call when the actual copy/move is requested
                this.ns_type = ns_type
                this.ns_key = ns_key

                // Construct the folder hierarchy
                this.namespace = [
                    {
                        name: "HOME",
                        id: -1, // Root is represented by '-1' when moving/copying into it via Namespace Service
                        parent_id: null,
                        color: '',
                        children: this.flatToHierarchy(folders, moved_folder_id)
                    }
                ]
            })
            .catch(err => {
                console.error(err)
                if(err.status && err.body){
                    this.error = "Failed to list folders: " + err.body
                }
            })
            .finally(() => this.namespace_loading = false)
        },

        submit(){
            if(!this.selected_folder || this.targetFile == undefined){ return }
            this.submit_loading = true
            this.error = false
            let promise = null

            switch(this.action){
                case 'copy_file':
                    promise = NamespaceService.copy_file(this.targetFile.id, this.selected_folder.id)
                    // copy folder is not supported
                    break;
                case 'move_file':
                case 'move_folder':
                    promise = NamespaceService.move_entity(
                        this.targetFile.id, 
                        this.selected_folder.id, 
                        this.ns_type,
                        this.ns_key
                    )
                    break;
                default:
                    this.error = "Unsupported action: '" + this.action + "'"
                    return;
            }
            promise.then(res => {
                // If the file/folder was moved to the root, then 'selected_folder.id' is -1 but 'targetFile.parent_id' should be set to null
                const new_parent_id = (this.selected_folder.id == -1) ? null : this.selected_folder.id
                
                const result = {
                    entity_id: this.targetFile.id,
                    action: this.action,
                    new_parent_id: new_parent_id,
                    new_file_id: res.body.id || null
                }
                
                Utils.show_success(`${this.targetFile.name} ${result.action.startsWith('copy') ? 'copied' : 'moved'}`)
                bus.$emit(events.COPY_MOVE_COMPLETED, result)
                this.$emit('close')

            }).catch(err => {
                console.error(err)
                this.error = "Error: " + (err.body?.message || "internal error")

            }).finally(() => {
                this.submit_loading = false
            })

        },

        flatToHierarchy(flat, moved_folder_id) {
            /* Source: https://stackoverflow.com/questions/31383869/converting-flat-structure-to-hierarchical */

            var roots = [] // folders that are directly in the root

            // make them accessible by guid on this map
            var all = {}

            flat.forEach(item => {
                all[item.id] = item
            })

            // connect childrens to its parent, and split roots apart
            Object.keys(all).forEach(folder_id => {
                const folder = all[folder_id]

                if(folder.id === moved_folder_id){
                    // do not add the moved folder to the tree (so it cannot be moved onto itself)
                    return
                }

                if (folder.parent_id === null) {
                    // root
                    roots.push(folder)
                } else if (folder.parent_id in all) {
                    var p = all[folder.parent_id]
                    if (!('children' in p)) {
                        p.children = []
                    }
                    p.children.push(folder)
                    Utils.sortByKeys(p.children, 'name', false, true)
                }
            })
            Utils.sortByKeys(roots, 'name', false, true)
            return roots
        },


        tree_folder_selected(folder){
            if(this.selected_folder){
                // Unselect current
                this.$set(this.selected_folder, 'selected', false)
            }
            // Invert selected 
            if(!this.selected_folder || folder.id != this.selected_folder.id){
                this.$set(folder, 'selected', !folder.selected)
            }
            // Update selected_folder
            this.selected_folder = folder.selected ? folder : null
        },
    }
}
</script>
<style scoped>
    .dd{
        margin: 0px auto;
        font-size: inherit;
        line-height: inherit;
    }
</style>
